import type { Quality } from '../api/types';
import { Async, PanelCard } from '../components/Panel';
import { Signal } from '../components/Signal';
import { ClampedText, Muted, Table } from '../components/Table';
import { TileRow, type TileSpec } from '../components/Tiles';
import { useApi } from '../hooks/useApi';
import { num, pct, plural, reasonAction, reasonLabel, shortDate } from '../lib/format';
import { useApp } from '../state/app';

/** The question that was rated. An absent one is not an em dash like any other empty cell: a
 *  rating whose turn is gone from `chat_events` is a fact about retention. */
function QuestionCell({ text }: { text: string | null }) {
  return <ClampedText text={text} missing={<Muted>not stored</Muted>} />;
}

/**
 * What each reason asks somebody to do about it, placed under the table it decodes. Only the
 * reasons actually picked in this window are listed.
 */
function ReasonLegend({ reasons }: { reasons: Quality['reasons'] }) {
  const entries = reasons
    .map((r) => ({ label: reasonLabel(r.reason), action: reasonAction(r.reason) }))
    .filter((e) => e.action);

  if (!entries.length) return null;

  return (
    <dl className="mt-3 flex flex-col gap-1 border-t border-[var(--border-light)] pt-3 text-xs text-[var(--text-muted)]">
      {entries.map((e) => (
        <div key={e.label} className="flex flex-wrap gap-x-2">
          <dt className="font-medium text-[var(--text-secondary)]">{e.label}</dt>
          <dd>— {e.action}</dd>
        </div>
      ))}
    </dl>
  );
}

/* There is no card for the 1-5 scores: the columns exist and `quality()` averages them, but
 * the assistant's feedback panel never sends a score, so a card would show a mean no user
 * gave. The query already has the numbers when a control that collects them exists.
 */

/**
 * What the ratings say, and how much weight they can carry. The count comes first, because a
 * helpful rate over nine ratings is an anecdote.
 */
function tilesFor(data: Quality, word: (full: string, plain: string) => string): TileSpec[] {
  const n = Number(data.totals.n ?? 0);
  const turns = Number(data.turns ?? 0);
  const top = data.reasons[0];

  return [
    {
      // First, because it is the caveat on the other three: a few per cent means this page
      // is a handful of opinions.
      label: word('Rating coverage', 'Answers people rated'),
      value: pct(turns ? n / turns : null, 1),
      hint: turns
        ? `${plural(n, 'rating')} on ${plural(turns, 'answer')} — everything below rests on this`
        : 'nothing was asked in this period',
    },
    {
      label: word('Ratings', 'Ratings given'),
      value: num(n),
      hint: !n
        ? 'nobody has rated an answer yet'
        : n < 5
          ? 'too few to conclude anything from'
          : 'thumbs given under an answer',
    },
    {
      label: word('Rated helpful', 'Answers people liked'),
      value: pct(data.totals.helpful_rate),
      hint: n ? `of ${plural(n, 'rating')} in this period` : 'nobody has rated an answer yet',
    },
    {
      // The count is the tile and the reason is the hint, since a reason label set in the
      // figure's type wraps to three lines and stops being a figure.
      label: word('Reasons picked', 'People who said why'),
      value: num(data.reasons.reduce((total, r) => total + Number(r.n || 0), 0)),
      hint: top
        ? `most often: ${reasonLabel(top.reason)} (${num(Number(top.n))})`
        : 'nobody has picked a reason',
    },
  ];
}

export function QualityPanel() {
  const { days, system, word } = useApp();
  const query = useApi<Quality>('/api/quality', { days, system });

  return (
    <Async query={query} shape="page">
      {(data) => (
          <div className="space-y-5">
            <TileRow tiles={tilesFor(data, word)} />

            {/* One table, one row per rating — the reason and the comment are two halves of
                the same sentence, and the reader was reading them side by side anyway. The
                counts per reason are on the tile above, which is where a total belongs. */}
            <PanelCard
              title="What people said about the answers"
              hint="Every rating that came with a reason or a comment, newest first. Each reason is a different piece of work — the legend under the table says which."
            >
              <Table
                rows={data.comments}
                // Ten of the fifty the endpoint returns: the rest is a queue to work
                // through, not a list to scan, and the page control says how much is behind.
                pageSize={10}
                search="Search what people wrote"
                empty="No ratings carried a reason or a comment in this window."
                columns={[
                  { id: 'ts', label: 'When', render: (r) => shortDate(r.ts), sortValue: (r) => r.ts },
                  {
                    id: 'helpful',
                    label: 'Rating',
                    sortValue: (r) => String(r.helpful),
                    render: (r) =>
                      r.helpful ? (
                        <Signal tone="good">✓ helpful</Signal>
                      ) : (
                        <Signal tone="critical">✕ not helpful</Signal>
                      ),
                  },
                  // Sortable, so the rows group by role in one click — which is the cheap
                  // half of "are the students unhappy or the staff", without a query for it.
                  { key: 'user_role', label: 'Role' },
                  {
                    id: 'query',
                    label: 'Question',
                    sortValue: (r) => r.query,
                    render: (r) => <QuestionCell text={r.query} />,
                  },
                  { id: 'reason', label: 'Reason', render: (r) => reasonLabel(r.reason) },
                  { key: 'comment', label: 'Comment' },
                ]}
              />
              <ReasonLegend reasons={data.reasons} />
            </PanelCard>

            {/* After the comments, not before them: the reader arrives at a file to open
                having just read why somebody was unhappy with it. */}
            <PanelCard
              title="Documents behind the unhelpful answers"
              simpleTitle="Documents the bad answers came from"
              hint="Documents used in answers people marked unhelpful, against every rating those documents got. Often the document is fine and the assistant reached for the wrong one."
              simpleHint="The documents the bad answers were built from. Where to look first — the document is not always the one at fault."
            >
              <Table
                // `?? []` because a server started before this query existed answers without
                // the field, and `rows.length` on the undefined would blank the whole page.
                rows={data.unhelpful_sources ?? []}
                empty="No unhelpful rating in this window could be traced back to a document."
                pageSize={10}
                columns={[
                  { key: 'source', label: 'Document' },
                  {
                    id: 'unhelpful',
                    label: 'Rated unhelpful',
                    num: true,
                    render: (r) => (
                      <Signal tone="critical">✕ {num(Number(r.unhelpful))}</Signal>
                    ),
                    sortValue: (r) => Number(r.unhelpful),
                  },
                  {
                    // The denominator that stops this being a list of the busiest files:
                    // three of four is a document to open, three of forty is a popular one.
                    id: 'rated',
                    label: 'Of answers rated',
                    num: true,
                    render: (r) => num(Number(r.rated)),
                    sortValue: (r) => Number(r.rated),
                  },
                  {
                    id: 'questions',
                    label: 'Questions',
                    num: true,
                    advanced: true,
                    render: (r) => num(Number(r.questions)),
                    sortValue: (r) => Number(r.questions),
                  },
                ]}
              />
            </PanelCard>
          </div>
      )}
    </Async>
  );
}
